import { api } from './api';

class CreateCaseService {
    /**
     * Get available cause of calls
     * @param {Object} params - Query parameters
     * @param {number} params.parent_id - Parent cause ID (optional)
     * @param {boolean} params.is_active - Filter by active status (default: true)
     * @returns {Promise} API response with cause of calls
     */
    async getCauseOfCalls(params = {}) {
        try {
            const queryString = new URLSearchParams(params).toString();
            const response = await api.get(`/api/cases/cause-of-calls/?${queryString}`);
            return {
                causes: response || [],
                success: true
            };
        } catch (error) {
            console.error('Error fetching cause of calls:', error);
            return {
                causes: [],
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Get available cause of calls for a specific scheme
     * @param {number} schemeId - Scheme ID
     * @param {Object} params - Query parameters
     * @param {number} params.parent_id - Parent cause ID (optional)
     * @param {boolean} params.is_active - Filter by active status (default: true)
     * @returns {Promise} API response with scheme cause of calls
     */
    async getSchemeCauseOfCalls(schemeId, params = {}) {
        try {
            const queryString = new URLSearchParams(params).toString();
            const response = await api.get(`/api/cases/schemes/${schemeId}/cause-of-calls/?${queryString}`);
            return {
                causes: response || [],
                success: true
            };
        } catch (error) {
            console.error('Error fetching scheme cause of calls:', error);
            return {
                causes: [],
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Get customer details including vehicles and policies
     * @param {number} customerId - Customer ID
     * @returns {Promise} API response with customer details, vehicles, and policies
     */
    async getCustomerDetails(customerId) {
        try {
            const response = await api.get(`/api/customers/${customerId}`);
            return {
                customer: response?.customer || null,
                vehicles: response?.vehicles || [],
                policies: response?.policies || [],
                recent_cases: response?.recent_cases || [],
                success: true
            };
        } catch (error) {
            console.error('Error fetching customer details:', error);
            return {
                customer: null,
                vehicles: [],
                policies: [],
                recent_cases: [],
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Get policy details by policy ID
     * @param {number} policyId - Policy ID
     * @returns {Promise} API response with policy details
     */
    async getPolicyDetails(policyId) {
        try {
            const response = await api.get(`/api/customers/policies/${policyId}`);
            return {
                policy: response?.policy || null,
                customer: response?.customer || null,
                vehicle: response?.vehicle || null,
                client_scheme: response?.client_scheme || null,
                utilization: response?.utilization || null,
                success: true
            };
        } catch (error) {
            console.error('Error fetching policy details:', error);
            return {
                policy: null,
                customer: null,
                vehicle: null,
                client_scheme: null,
                utilization: null,
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Get vehicle details by vehicle ID
     * @param {number} vehicleId - Vehicle ID
     * @returns {Promise} API response with vehicle details
     */
    async getVehicleDetails(vehicleId) {
        try {
            const response = await api.get(`/api/customers/vehicles/${vehicleId}`);
            return {
                vehicle: response?.vehicle || null,
                customer: response?.customer || null,
                active_policy: response?.active_policy || null,
                case_statistics: response?.case_statistics || {},
                success: true
            };
        } catch (error) {
            console.error('Error fetching vehicle details:', error);
            return {
                vehicle: null,
                customer: null,
                active_policy: null,
                case_statistics: {},
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Create a new case
     * @param {Object} caseData - Case creation payload
     * @param {number} caseData.customer_id - Customer ID
     * @param {number} caseData.vehicle_id - Vehicle ID (optional)
     * @param {string} caseData.vehicle_registration - Vehicle registration number (optional)
     * @param {number} caseData.client_id - Client ID
     * @param {number} caseData.client_scheme_id - Client scheme ID
     * @param {number} caseData.scheme_cause_of_call_id - Scheme cause of call ID
     * @param {string} caseData.description - Case description
     * @returns {Promise} API response with created case
     */
    async createCase(caseData) {
        try {
            const response = await api.post('/api/cases/', caseData);
            return {
                case: response,
                success: true
            };
        } catch (error) {
            console.error('Error creating case:', error);
            throw error; // Re-throw to maintain existing error handling in component
        }
    }

    /**
     * Get a case by ID
     * @param {number} caseId - Case ID
     * @returns {Promise} API response with case details
     */
    async getCaseById(caseId) {
        try {
            const response = await api.get(`/api/admin/cases/${caseId}`);
            return {
                case: response,
                success: true
            };
        } catch (error) {
            console.error('Error fetching case:', error);
            return {
                case: null,
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Update an existing case
     * @param {number} caseId - Case ID
     * @param {Object} updateData - Case update payload
     * @returns {Promise} API response with updated case
     */
    async updateCase(caseId, updateData) {
        try {
            const response = await api.put(`/api/admin/cases/${caseId}`, updateData);
            return {
                case: response,
                success: true
            };
        } catch (error) {
            console.error('Error updating case:', error);
            throw error;
        }
    }

    /**
     * Get cases with pagination and filters
     * @param {Object} params - Query parameters
     * @param {number} params.page - Page number
     * @param {number} params.limit - Items per page
     * @param {string} params.status - Filter by status
     * @param {number} params.customer_id - Filter by customer ID
     * @returns {Promise} API response with cases list
     */
    async getCases(params = {}) {
        try {
            const queryString = new URLSearchParams(params).toString();
            const response = await api.get(`/api/admin/cases?${queryString}`);
            return {
                cases: response?.items || [],
                total: response?.total || 0,
                page: response?.page || 1,
                limit: response?.limit || 20,
                pages: response?.pages || 1,
                success: true
            };
        } catch (error) {
            console.error('Error fetching cases:', error);
            return {
                cases: [],
                total: 0,
                page: 1,
                limit: 20,
                pages: 1,
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Delete a case
     * @param {number} caseId - Case ID
     * @returns {Promise} API response
     */
    async deleteCase(caseId) {
        try {
            await api.delete(`/api/admin/cases/${caseId}`);
            return {
                success: true
            };
        } catch (error) {
            console.error('Error deleting case:', error);
            throw error;
        }
    }

    /**
     * Add a note to a case
     * @param {number} caseId - Case ID
     * @param {Object} noteData - Note data
     * @param {string} noteData.content - Note content
     * @param {string} noteData.note_type - Type of note
     * @returns {Promise} API response with created note
     */
    async addCaseNote(caseId, noteData) {
        try {
            const response = await api.post(`/api/cases/${caseId}/notes`, noteData);
            return {
                note: response,
                success: true
            };
        } catch (error) {
            console.error('Error adding case note:', error);
            throw error;
        }
    }

    /**
     * Get notes for a case
     * @param {number} caseId - Case ID
     * @returns {Promise} API response with case notes
     */
    async getCaseNotes(caseId) {
        try {
            const response = await api.get(`/api/cases/${caseId}/notes`);
            return {
                notes: response?.notes || [],
                success: true
            };
        } catch (error) {
            console.error('Error fetching case notes:', error);
            return {
                notes: [],
                success: false,
                error: error.message
            };
        }
    }
}

export const createCaseService = new CreateCaseService(); 